import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Grid, Stack, Box, Alert, CircularProgress } from "@mui/material";
import { arrayMove } from "@dnd-kit/sortable";
// import LockIcon from '@mui/icons-material/Lock';
// import LockOpenIcon from "@mui/icons-material/LockOpen";
import { ReactComponent as AlertChainIcon } from "../../assets/alert_chain.svg";
import { ReactComponent as LockOpenIcon } from "../../assets/AmazonLockOpen.svg";
import { ReactComponent as LockIcon } from "../../assets/AmazonLock.svg";


import ProductCard from "./partials/ProductCard";
import ProfitCalculator from "./partials/ProfitCalculator";
import Chart from "./partials/Chart";
import QuickInfo from "./partials/QuickInfoCard";
import RankAndPrice from "./partials/RankAndPrice";
import Alerts from "./partials/Alerts";
import Discounts from "./partials/Discounts";
import Notes from "./partials/Notes";
import Marketplaces from "./partials/Marketplaces";
import SellerCentral from "./partials/SellerCentral";
import SearchAgain from "./partials/SearchAgain";
import Offers from "./partials/Offers";
import BuyBoxAnalysis from "./partials/BuyBoxAnalysis";
import AdditionalExpenses from "./partials/AdditionalExpenses";
import Variations from "./partials/Variations";
import Keepa from "./partials/Keepa";
import Roi from "./partials/Roi";
import ASINStatistics from "./partials/ASINStatistics";
import History from "./partials/History";
import StyledAccordion, { TCardPosition } from "./common/StyledAccordion";
import { IconButton, Tooltip } from "../../components";


import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  selectAlerts,
  selectProductDetail,
  selectProductHistories,
  selectRestrictions,
} from "../../features/productAnalyzer/productAnalyzer.selector";
import { selectAmazonAccount } from "../../features/user/user.selector";
import {
  ProductAnalyzerProvider,
  useProductAnalyzer,
} from "../../contexts/productAnalyzerContext";
import useBreakpoint from "../../hooks/useBreakpoint";
import {
  selectPanelOrder,
  selectProductAnalyzerSettings,
} from "../../features/settings/settings.selector";
import {
  BUTTON_COLORS,
  INIT_PANELS,
  PRODUCT_ANALYZER_COLUMNS,
  Panel,
} from "../../constants";


import BoltIcon from "../../assets/productAnalyzer/bolt.svg";
import BoltBlueIcon from "../../assets/productAnalyzer/bolt-blue.svg";
import HashtagIcon from "../../assets/productAnalyzer/hashtag.svg";
import HashtagBlueIcon from "../../assets/productAnalyzer/hashtag-blue.svg";
import BellIcon from "../../assets/productAnalyzer/bell.svg";
import BellBlueIcon from "../../assets/productAnalyzer/bell-blue.svg";
import PieChartIcon from "../../assets/productAnalyzer/pie-chart.svg";
import PieChartBlueIcon from "../../assets/productAnalyzer/pie-chart-blue.svg";
import MoneyBagIcon from "../../assets/productAnalyzer/money-bag.svg";
import MoneyBagBlueIcon from "../../assets/productAnalyzer/money-bag-blue.svg";
import BarChartIcon from "../../assets/productAnalyzer/bar-chart.svg";
import BarChartBlueIcon from "../../assets/productAnalyzer/bar-chart-blue.svg";
import CalculatorIcon from "../../assets/productAnalyzer/calculator.svg";
import CalculatorBlueIcon from "../../assets/productAnalyzer/calculator-blue.svg";
import GridIcon from "../../assets/productAnalyzer/grid.svg";
import GridBlueIcon from "../../assets/productAnalyzer/grid-blue.svg";
import SearchIcon from "../../assets/productAnalyzer/search.svg";
import SearchBlueIcon from "../../assets/productAnalyzer/search-blue.svg";
import PlanetIcon from "../../assets/productAnalyzer/planet.svg";
import PlanetBlueIcon from "../../assets/productAnalyzer/planet-blue.svg";
import AmazonIcon from "../../assets/productAnalyzer/amazon.svg";
import AmazonBlueIcon from "../../assets/productAnalyzer/amazon-blue.svg";
import TableIcon from "../../assets/productAnalyzer/table.svg";
import TableBlueIcon from "../../assets/productAnalyzer/table-blue.svg";
import PlusIcon from "../../assets/productAnalyzer/plus.svg";
import NotesIcon from "../../assets/productAnalyzer/notes.svg";
import PercentIcon from "../../assets/productAnalyzer/percent.svg";
import CartIcon from "../../assets/productAnalyzer/cart.svg";
import LineChartIcon from "../../assets/productAnalyzer/line-chart.svg";


import { useStyles } from "./common/StyledAccordion/index.styles";
import { updateProductAnalyzerSettingsThunk } from "../../features/settings/settings.thunk";


type TPanel = {
  component: React.ReactNode;
  Icon?: string;
  jumpIcon?: string;
  isCollapsible?: boolean;
};
const PANELS: Record<Panel, TPanel> = {
  [Panel.PRODUCT]: { component: <ProductCard />, isCollapsible: true },
  [Panel.QUICK_INFO]: {
    component: <QuickInfo />,
    isCollapsible: false,
    Icon: BoltIcon,
    jumpIcon: BoltBlueIcon,
  },
  [Panel.PROFIT_CALCULATOR]: {
    component: <ProfitCalculator />,
    isCollapsible: true,
    Icon: CalculatorIcon,
    jumpIcon: CalculatorBlueIcon,
  },
  [Panel.CHARTS]: {
    component: <Chart />,
    Icon: BarChartIcon,
    jumpIcon: BarChartBlueIcon,
  },
  [Panel.RANKS_AND_PRICES]: {
    component: <RankAndPrice />,
    isCollapsible: true,
    Icon: HashtagIcon,
    jumpIcon: HashtagBlueIcon,
  },
  [Panel.ALERTS]: {
    component: <Alerts />,
    isCollapsible: true,
    Icon: BellIcon,
    jumpIcon: BellBlueIcon,
  },
  [Panel.DISCOUNTS]: { component: <Discounts />, Icon: PercentIcon },
  [Panel.NOTES_AND_TAGS]: { component: <Notes />, Icon: NotesIcon },
  [Panel.MARKETPLACES]: {
    component: <Marketplaces />,
    isCollapsible: true,
    Icon: PlanetIcon,
    jumpIcon: PlanetBlueIcon,
  },
  [Panel.SELLER_CENTRAL]: {
    component: <SellerCentral />,
    Icon: AmazonIcon,
    jumpIcon: AmazonBlueIcon,
  },
  [Panel.SEARCH_AGAIN]: { component: <SearchAgain />, isCollapsible: true },
  [Panel.OFFERS]: { component: <Offers />, Icon: CartIcon },
  [Panel.BUY_BOX_ANALYSIS]: {
    component: <BuyBoxAnalysis />,
    isCollapsible: true,
    Icon: PieChartIcon,
    jumpIcon: PieChartBlueIcon,
  },
  [Panel.ADDITIONAL_EXPENSES]: {
    component: <AdditionalExpenses />,
    isCollapsible: true,
    Icon: MoneyBagIcon,
    // jumpIcon: MoneyBagBlueIcon,
  },
  [Panel.VARIATIONS]: {
    component: <Variations />,
    isCollapsible: true,
    Icon: GridIcon,
    jumpIcon: GridBlueIcon,
  },
  [Panel.KEEPA]: { component: <Keepa />, isCollapsible: true, Icon: PlusIcon },
  [Panel.ROI]: {
    component: <Roi />,
    isCollapsible: true,
    Icon: TableIcon,
    jumpIcon: TableBlueIcon,
  },
  [Panel.ASIN_STATISTIC]: {
    component: <ASINStatistics />,
    Icon: LineChartIcon,
  },
  [Panel.HISTORY]: {
    component: <History />,
    isCollapsible: true,
    Icon: SearchIcon,
    jumpIcon: SearchBlueIcon,
  },
};


type AlertLevel = "success" | "warning" | "danger";


const COLORS = {
  success: "#22C55E",
  warning: "#DAA520",
  danger: "#EF4444",
};


// const FONT_COLORS = {
//   success: "#E9FBEF",
//   warning: "#FFF4D9",
//   danger: "#F2DEDE",
// };


const FONT_COLORS = {
  success: "#FFFFFF",
  warning: "#FFFFFF",
  danger: "#FFFFFF",
};


const getAlertAbbr = (msg: string = "") => {
  if (msg.includes("Only one seller has had BB")) {
    return "BB";
  } else if (msg.includes("Private Label")) {
    return "PL";
  } else if (msg.includes("IP Analysis")) {
    return "IP";
  } else if (msg.includes("Oversize - Small") || msg.includes("Over Size")) {
    return "S";
  } else {
    return msg.slice(0, 1).toUpperCase();
  }
};

const EligibilityIcon = () => {
  const { setOpenQuickList } = useProductAnalyzer();
  const { restrictions, loadingRestrictions } = useAppSelector(selectRestrictions);
  const { isConnected } = useAppSelector(selectAmazonAccount);

  const isRestricted =
    (restrictions?.restrictionResult?.restrictions?.length ?? 0) > 0;
  const tooltipTitle = loadingRestrictions
    ? "Checking eligibility..."
    : !isConnected
      ? "Not Connected"
      : isRestricted
        ? "Not eligible to sell"
        : "Eligible to sell";

  return (
    <Tooltip title={tooltipTitle}>
      <Box
        onClick={!loadingRestrictions && !isConnected ? () => setOpenQuickList(true) : undefined}
        sx={{
          borderRadius: "4px",
          width: "20px",
          height: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: !loadingRestrictions && !isConnected ? "1px solid #B12704" : "1px solid #C9C9C9",
          backgroundColor: "#F0F0F0",
          fontSize: "10px",
          fontWeight: 600,
          cursor: !loadingRestrictions && !isConnected ? "pointer" : "default",
        }}
      >
        {loadingRestrictions ? (
          <CircularProgress size={12} sx={{ color: "#888" }} />
        ) : !isConnected ? (
          <AlertChainIcon
            style={{ width: "18px", height: "16px", display: "block" }}
          />
        ) : isRestricted ? (
          <LockIcon
            style={{
              width: "18px",
              height: "16px",
              display: "block",
            }}
          />
        ) : (
          <LockOpenIcon
            style={{
              width: "18px",
              height: "16px",
              display: "block",
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export const Panels = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { breakpoint } = useBreakpoint();


  const panelOrder = useAppSelector(selectPanelOrder);
  const { showOffersChart, showPricesChart, showSoldChart, showJumpButtons } =
    useAppSelector(selectProductAnalyzerSettings);
  const { alerts } = useAppSelector(selectAlerts);
  const { total: historyCount } = useAppSelector(selectProductHistories);


  const [orders, setOrders] = useState<Panel[][]>();
  const panelRefs = useRef<Record<Panel, HTMLElement | null>>({
    [Panel.PRODUCT]: null,
    [Panel.QUICK_INFO]: null,
    [Panel.PROFIT_CALCULATOR]: null,
    [Panel.CHARTS]: null,
    [Panel.RANKS_AND_PRICES]: null,
    [Panel.ALERTS]: null,
    [Panel.DISCOUNTS]: null,
    [Panel.NOTES_AND_TAGS]: null,
    [Panel.MARKETPLACES]: null,
    [Panel.SELLER_CENTRAL]: null,
    [Panel.SEARCH_AGAIN]: null,
    [Panel.OFFERS]: null,
    [Panel.BUY_BOX_ANALYSIS]: null,
    [Panel.ADDITIONAL_EXPENSES]: null,
    [Panel.VARIATIONS]: null,
    [Panel.KEEPA]: null,
    [Panel.ROI]: null,
    [Panel.ASIN_STATISTIC]: null,
    [Panel.HISTORY]: null,
  });


  useEffect(() => {
    if (!breakpoint) return;
    setOrders((prev) => {
      const arr =
        prev?.reduce((res, col) => [...res, ...col], []) ||
        panelOrder
          .filter(
            (item) =>
              item.enabled &&
              (Number(item.id) !== Panel.CHARTS ||
                showOffersChart ||
                showPricesChart ||
                showSoldChart),
          )
          .map((item) => item.id);
      const columns =
        PRODUCT_ANALYZER_COLUMNS[
          breakpoint as keyof typeof PRODUCT_ANALYZER_COLUMNS
        ];
      const rows = Math.floor(arr.length / columns);
      return [...new Array(columns)].map((i, index) => {
        const remaining = arr.length - rows * columns;
        const addOneMore = remaining > index;
        const start = index * rows + (addOneMore ? index : remaining);
        const end = start + rows - 1 + (addOneMore ? 1 : 0);
        return arr.slice(start, end + 1);
      });
    });
  }, [breakpoint, showOffersChart, showPricesChart, showSoldChart]);


  const handleDragDrop = useCallback(
    (from: TCardPosition, to: TCardPosition) => {
      const fromKey = orders?.[from.x]?.[from.y];
      const toKey = orders?.[to.x]?.[to.y];
      if (!fromKey || !toKey) return;


      setOrders((prev) => {
        if (!prev) return prev;
        let updated = [...prev];
        if (!toKey) {
          updated[to.x].push(fromKey);
        } else if (from.x === to.x && to.y > from.y) {
          updated[to.x].splice(to.y + 1, 0, fromKey);
        } else {
          updated[to.x].splice(to.y, 0, fromKey);
        }
        if (from.x === to.x && to.y < from.y) {
          updated[from.x].splice(from.y + 1, 1);
        } else {
          updated[from.x].splice(from.y, 1);
        }
        return updated;
      });
      const fromIndex = panelOrder.findIndex((panel) => panel.id === fromKey);
      const toIndex = panelOrder.findIndex((panel) => panel.id === toKey);
      dispatch(
        updateProductAnalyzerSettingsThunk({
          panelOrder: arrayMove(panelOrder, fromIndex, toIndex),
        }),
      );
    },
    [panelOrder],
  );


  // const renderAlertMessage = () => {
  //   if (isAmazonCredentials) {
  //     return (
  //       <Alert severity="error">
  //         Amazon credentials are missing. Please add them in the settings to
  //         continue.{" "}
  //         <button
  //           style={{
  //             cursor: "pointer",
  //             textDecoration: "underline",
  //             border: "none",
  //             padding: "0px",
  //             background: "none",
  //           }}
  //           onClick={() => {
  //             navigate("/settings/amazon-credentials");
  //             localStorage.setItem("AmazonCredentialsModal", "false");
  //           }}
  //           onKeyDown={(e) => {
  //             if (e.key === "Enter" || e.key === " ") {
  //               navigate("/settings/amazon-credentials");
  //               localStorage.setItem("AmazonCredentialsModal", "false");
  //             }
  //           }}
  //         >
  //           Go to setting
  //         </button>
  //       </Alert>
  //     );
  //   }
  //   return null;
  // };


  const renderBadge = (id: Panel) => {
    const panelId = Number(id);


    if (panelId === Panel.HISTORY) {
      return null;
    }


    if (panelId === Panel.QUICK_INFO) {
      let successAlerts: string[] = [];
      let warningAlerts: string[] = [];
      let dangerAlerts: string[] = [];


      alerts?.alertLevel?.forEach((item: any) => {
        if (item.level === "success") {
          successAlerts = successAlerts.concat(item.message || []);
        } else if (item.level === "warning") {
          warningAlerts = warningAlerts.concat(item.message || []);
        } else if (item.level === "danger") {
          dangerAlerts = dangerAlerts.concat(item.message || []);
        }
      });


      const renderAlertChip = (messages: string[], level: AlertLevel) => {
        const abbr =
          messages.length > 1
            ? messages.length.toString()
            : getAlertAbbr(messages[0]);
        const isLock =
          abbr === "LOCK" ||
          messages[0]?.toLowerCase().includes("lock") ||
          messages[0]?.toLowerCase().includes("restricted");


        return (
          <Tooltip
            key={`${level}-${messages.join("-")}`}
            title={
              <Box>
                {messages.map((msg, index) => (
                  <Box key={`${level}-${index}`}>{msg}</Box>
                ))}
              </Box>
            }
          >
            <Box
              sx={{
                borderRadius: "4px",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: COLORS[level],
                color: FONT_COLORS[level],
                fontSize: "10px",
                fontWeight: 600,
              }}
            >
              {isLock ? (
                <LockIcon
                  style={{
                    width: "18px",
                    height: "16px",
                    display: "block",
                  }}
                />
              ) : (
                abbr
              )}
            </Box>
          </Tooltip>
        );
      };


      return (
        <Box
          sx={{
            minWidth: "140px",
            minHeight: "24px",
            borderRadius: "4px",
            border: "1px solid #FDE68A",
            backgroundColor: "#FFFBE8",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            paddingRight: "3px",
            paddingLeft: "4px",
            py: "2px",
            gap: "2px",
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              color: "#B45309",
              fontSize: "12px",
              lineHeight: "18px",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              marginRight: "4px",
            }}
          >
            Alerts
          </Box>
          <Box display="flex" alignItems="center" gap="2px">
            {warningAlerts.map((alert) => renderAlertChip([alert], "warning"))}
            {dangerAlerts.map((alert) => renderAlertChip([alert], "danger"))}
            {!warningAlerts.length &&
              !dangerAlerts.length &&
              !!successAlerts.length &&
              renderAlertChip(successAlerts, "success")}
            <EligibilityIcon />
          </Box>
        </Box>
      );
    }


    if (id === Panel.ALERTS) {
      return (
        <Box display={"flex"} gap="4px">
          {alerts?.variations &&
            alerts?.alertLevel.map((item: any, index: number) => {
              const level = item.level as AlertLevel;
              return (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                  key={index}
                >
                  <Tooltip
                    title={
                      <Box>
                        {item.message.map((me: string, i: number) => (
                          <Box key={index + "-" + i}>{me}</Box>
                        ))}
                      </Box>
                    }
                  >
                    <Box
                      className={`${classes.badge} ${classes[`${level}Badge`]}`}
                    >
                      {item.message.length}
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
        </Box>
      );
    }
  };


  const renderJumpIcons = () => {
    return (
      <Box display="flex" flexWrap="wrap" gap="4px">
        {panelOrder
          .filter((panel) => panel.enabled && !!PANELS[panel.id].jumpIcon)
          .map((panel) => (
            <Tooltip title={panel.title}>
              <IconButton
                size="small"
                color="secondary"
                onClick={() =>
                  panelRefs.current[panel.id]?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  })
                }
                sx={{
                  background: BUTTON_COLORS.secondaryHover,
                }}
              >
                <img
                  src={PANELS[panel.id].jumpIcon}
                  alt={`${panel.title} Icon`}
                />
              </IconButton>
            </Tooltip>
          ))}
      </Box>
    );
  };


  return (
    <ProductAnalyzerProvider>
      <DndProvider backend={HTML5Backend}>
        <Grid
          container
          spacing={1}
          sx={{
            overflowX: "visible",
            flexWrap: "wrap",
            "@media (max-width:319px)": {
              overflowX: "auto",
              flexWrap: "nowrap",
            },
          }}
        >
          {orders?.map((cards, x) => (
            <Grid
              size={{
                xl: 12 / PRODUCT_ANALYZER_COLUMNS.xl,
                lg: 12 / PRODUCT_ANALYZER_COLUMNS.lg,
                md: 12 / PRODUCT_ANALYZER_COLUMNS.md,
                sm: 12 / PRODUCT_ANALYZER_COLUMNS.sm,
                xs: 12 / PRODUCT_ANALYZER_COLUMNS.xs,
              }}
              sx={{
                minWidth: "auto",
                "@media (max-width:319px)": {
                  minWidth: "320px",
                },
              }}
              key={x}
            >
              <Stack spacing={1}>
                {x === 0 &&
                  orders.length === 1 &&
                  showJumpButtons &&
                  renderJumpIcons()}
                {cards.map((key, y) => (
                  <StyledAccordion
                    ref={(el) => (panelRefs.current[key] = el)}
                    key={key}
                    id={key}
                    x={x}
                    y={y}
                    title={INIT_PANELS[key]?.title}
                    Icon={PANELS[key]?.Icon}
                    isCollapsible={PANELS[key]?.isCollapsible ?? true}
                    badge={renderBadge(key)}
                    onDrop={handleDragDrop}
                  >
                    {PANELS[key]?.component}
                  </StyledAccordion>
                ))}
                <StyledAccordion
                  key={`placeholder-${x}`}
                  id="EMPTY_PLACEHOLDER"
                  x={x}
                  y={cards.length}
                  onDrop={handleDragDrop}
                />
              </Stack>
            </Grid>
          ))}
        </Grid>
      </DndProvider>
    </ProductAnalyzerProvider>
  );
};


export default Panels;

